import { memo } from "react";
import ReactECharts from "echarts-for-react";
import {
  buildValueMap,
  buildWaterfallRows,
  selectPeriod,
  findLatestPeriod,
} from "../../lib/incomeStatementGraph";

// Ported visual logic from earnings-shorts/chart_template.html — horizontal
// waterfall: each cost row "starts" at the cumulative spine value so the
// chart reads top → bottom as a cascade of income → costs → next income.

function formatValue(v) {
  if (v == null || isNaN(v)) return 'N/A';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
}

function IncomeStatementWaterfall({
  data,
  sankeyMapData,
  isQuarterly = true,
  height,        // optional override; auto-sized by row count otherwise
}) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">No income statement data</p>;
  }

  const latest = findLatestPeriod(data, isQuarterly);
  if (!latest) {
    return <p className="text-sm text-gray-400 py-8 text-center">No period available</p>;
  }

  const { periodData, label } = selectPeriod(data, latest.fy, latest.fp);
  const valueMap = buildValueMap(periodData, sankeyMapData || []);
  const rows = buildWaterfallRows(valueMap, sankeyMapData || []);

  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 py-8 text-center">Insufficient data for {label}</p>;
  }

  // Build stacked-bar data: an invisible offset segment + the visible value.
  // For "income" rows the bar starts at 0 (full width from baseline).
  // For "cost" rows the bar starts at (curSpine - value) so it lines up like a waterfall step.
  let curSpine = 0;
  const offsets = [];
  const values = [];
  const colors = [];
  rows.forEach(r => {
    if (r.type === 'income') {
      offsets.push(0);
      values.push(r.value);
      colors.push('income');
      curSpine = r.value;
    } else {
      const start = Math.max(0, curSpine - r.value);
      offsets.push(start);
      values.push(r.value);
      colors.push('cost');
    }
  });

  // Auto-height: ~44 px per row, with sensible bounds.
  const computedHeight = height || `${Math.max(280, Math.min(720, rows.length * 44 + 40))}px`;

  const labels = rows.map(r => r.label);
  const formLabel = isQuarterly ? '10-Q · QUARTERLY' : '10-K · ANNUAL';

  const option = {
    backgroundColor: 'transparent',
    grid: { top: 8, bottom: 30, left: 100, right: 56, containLabel: false },
    xAxis: {
      type: 'value',
      axisLabel: { color: '#9ca3af', fontSize: 10, formatter: v => formatValue(v) },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
      axisLine: { lineStyle: { color: '#374151' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: labels,
      inverse: true,
      axisLabel: {
        color: '#e2e8f0',
        fontSize: 11,
        fontWeight: 600,
        align: 'right',
        width: 92,
        overflow: 'break',
        interval: 0,
      },
      axisLine: { lineStyle: { color: '#374151' } },
      axisTick: { show: false },
    },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#111827',
      borderColor: '#374151',
      textStyle: { color: '#e5e7eb', fontSize: 11 },
      formatter: p => p.seriesIndex === 0 ? '' : `<b>${p.name}</b><br/>${formatValue(p.value)}`,
    },
    series: [
      {
        // Invisible offset (left padding before the visible bar)
        type: 'bar',
        stack: 'wf',
        silent: true,
        itemStyle: { color: 'transparent' },
        data: offsets,
      },
      {
        // Visible value segment, gradient by income/cost
        type: 'bar',
        stack: 'wf',
        barMaxWidth: 26,
        data: values.map((v, i) => {
          const isIncome = colors[i] === 'income';
          return {
            value: v,
            itemStyle: {
              color: {
                type: 'linear', x: 1, y: 0, x2: 0, y2: 0,
                colorStops: [
                  { offset: 0, color: isIncome ? '#2ecc87' : '#e07474' },
                  { offset: 1, color: isIncome ? '#064e3b' : '#7f1d1d' },
                ],
              },
              borderRadius: [0, 6, 6, 0],
            },
          };
        }),
        label: {
          show: true,
          position: 'right',
          color: '#ffffff',
          fontSize: 11,
          fontWeight: 700,
          formatter: p => formatValue(p.value),
        },
      },
    ],
  };

  return (
    <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white tracking-wide">Income Statement</h3>
        <div className="px-3 py-1 rounded-md border border-blue-500/40 bg-blue-500/10 text-right">
          <div className="text-blue-400 font-bold text-xs">{label}</div>
          <div className="text-[9px] text-blue-300/70">{formLabel}</div>
        </div>
      </div>
      <ReactECharts option={option} style={{ height: computedHeight, width: '100%' }} />
    </div>
  );
}

export default memo(IncomeStatementWaterfall);
